import React, { useMemo, useEffect } from "react";
import type { GetServerDataProps } from "gatsby";

import { useTracker } from "@dangreaves/react-tracker";

import { MetaComponent } from "@bared/ui";

import {
  PageHomepageSchema,
  PageHomepageSaleSchema,
  PageHomepageVIPSchema,
  filterProjection,
} from "@bared/groq";

import type { HeadProps, PageProps } from "~/types/gatsby";

import { useSale } from "~/hooks/useSale";
import { useTripleWhale } from "~/hooks/useTripleWhale";

import { seoConfigForPage, getSeoMeta } from "~/lib/seo";
import { contextFromServerDataProps } from "~/lib/server";

import { SectionResolver } from "~/lib/SectionResolver";

import { ErrorPage } from "~/components/Error/Error";
import { Sections } from "~/components/Sections/Sections";

const pageHomepageQuery = filterProjection(
  PageHomepageSchema,
  `_type == "pageHomepage" && i18n_lang == $locale`,
);
const pageHomepageVIPQuery = filterProjection(
  PageHomepageVIPSchema,
  `_type == "pageHomepageVIP" && i18n_lang == $locale`,
);
const pageHomepageSaleQuery = filterProjection(
  PageHomepageSaleSchema,
  `_type == "pageHomepageSale" && i18n_lang == $locale`,
);

export async function getServerData(props: GetServerDataProps) {
  // Resolve context.
  const context = await contextFromServerDataProps(props);

  // Expand context.
  const { respond, notFound, groq, locale, site, storefront } = context;

  // Fetch homepages from sanity.
  const [
    pageDefault,
    pageLocalised,
    pageSaleDefault,
    pageSaleLocalised,
    pageVIPDefault,
    pageVIPLocalised,
  ] = await Promise.all([
    groq(pageHomepageQuery, { locale: null }),
    groq(pageHomepageQuery, { locale: locale.identifier }),
    groq(pageHomepageSaleQuery, { locale: null }),
    groq(pageHomepageSaleQuery, { locale: locale.identifier }),
    groq(pageHomepageVIPQuery, { locale: null }),
    groq(pageHomepageVIPQuery, { locale: locale.identifier }),
  ]);

  // Pick pages.
  const page = pageLocalised ?? pageDefault;
  const pageVIP = pageVIPLocalised ?? pageVIPDefault;
  const pageSale = pageSaleLocalised ?? pageSaleDefault;

  // Page not found in Sanity, return 404.
  if (!page) return notFound();

  // Resolve seo config.
  const seo = seoConfigForPage({
    context,
    sanityMetadata: page.metadata,
  });

  page.content = await new SectionResolver({
    storefront,
    siteHandle: site.handle,
    sanitySections: page.content,
  }).resolve();

  if (pageVIP) {
    pageVIP.content = await new SectionResolver({
      storefront,
      siteHandle: site.handle,
      sanitySections: pageVIP.content,
    }).resolve();
  }

  if (pageSale) {
    pageSale.content = await new SectionResolver({
      storefront,
      siteHandle: site.handle,
      sanitySections: pageSale.content,
    }).resolve();
  }

  // Respond with server data.
  return respond({ page, pageSale, pageVIP, seo } as const);
}

export type ServerData = Awaited<ReturnType<typeof getServerData>>["props"];

export default function HomePage({ serverData }: PageProps<ServerData>) {
  const { sale, isSaleActive } = useSale();

  const tracker = useTracker();
  const tripleWhale = useTripleWhale();

  // Send tracking events when server data changes.
  useEffect(() => {
    if (200 !== serverData.status) return;

    const { page } = serverData;

    tripleWhale.trackPageLoad("Homepage");

    tracker.page("Homepage", page.title);
  }, [tracker, tripleWhale, serverData]);

  /**
   * Select the sale page if needed.
   * @todo This will move to the server when we are able to access VIP status there.
   */
  const page = useMemo(() => {
    // Server returned an error response.
    if (200 !== serverData.status) return null;

    // No sale active, return the normal page.
    if (!sale || !isSaleActive()) return serverData.page;

    // Determine the sale type.
    const saleType = ["vip", "vip-test"].includes(sale.type) ? "vip" : "sale";

    // Try to resolve the VIP page, falling back to the ordinary sale page.
    const salePage =
      "vip" === saleType
        ? (serverData.pageVIP ?? serverData.pageSale)
        : serverData.pageSale;

    // Return the sale page if any resolved, otherwise the normal page.
    return salePage ?? serverData.page;
  }, [sale, isSaleActive, serverData]);

  // Page found, render page.
  if (!!page) {
    return <Sections page={page} />;
  }

  // Page not found, render error page.
  if (404 === serverData.status) {
    return <ErrorPage page={serverData.errorPage} />;
  }

  return null;
}

export function Head({ serverData }: HeadProps<ServerData>) {
  if (!serverData) return null;
  return (
    <MetaComponent
      meta={getSeoMeta(serverData.context.rootData.seo, serverData.seo)}
    />
  );
}
